import React from "react";
import { motion } from "framer-motion";
import { ArrowDown, Download, Github, Linkedin, Terminal as TerminalIcon, Command, GitBranch, CheckCircle2, Play } from "lucide-react";
import Avatar3D from "./Avatar3D";
import Terminal from "./Terminal";
import { openIntroVideo } from "./IntroVideoSection";
import { personalInfo, terminalLines, heroStack } from "../data/mock";

const Hero = () => {
  return (
    <section
      id="hero"
      className="relative min-h-screen w-full overflow-hidden pt-32 md:pt-36 pb-20"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-grid opacity-60" />
      <div className="aurora w-[560px] h-[560px] -top-40 -left-24 bg-cyan-500/25" />
      <div className="aurora w-[460px] h-[460px] top-40 -right-24 bg-amber-500/15" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#050507] to-transparent" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8">
        {/* Top meta row */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="flex flex-wrap items-center gap-3 mb-8"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass">
            <CheckCircle2 className="w-3 h-3 text-emerald-400" />
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-300">
              build: passing
            </span>
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass">
            <GitBranch className="w-3 h-3 text-cyan-300" />
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400">
              main · v1.0.0
            </span>
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
            </span>
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-emerald-300">
              {personalInfo.status}
            </span>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          {/* LEFT: Typography + Terminal */}
          <div className="lg:col-span-7 z-10">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="font-mono text-xs tracking-[0.25em] uppercase text-cyan-300/80 mb-4"
            >
              &gt; init portfolio — user:vedant
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="font-display font-semibold text-white leading-[0.95] tracking-tight"
            >
              <span className="block text-[11vw] sm:text-[9vw] lg:text-[7rem] xl:text-[8rem]">
                VEDANT
              </span>
              <span className="block text-[11vw] sm:text-[9vw] lg:text-[7rem] xl:text-[8rem] bg-gradient-to-r from-cyan-300 via-cyan-200 to-amber-200 bg-clip-text text-transparent">
                BORA.
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="mt-5 text-lg md:text-xl text-zinc-300 max-w-xl leading-relaxed"
            >
              {personalInfo.shortBio}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.65 }}
              className="mt-8 flex flex-wrap items-center gap-3"
            >
              <a
                href="#projects"
                className="group inline-flex items-center gap-2 px-6 h-12 rounded-xl bg-cyan-400 hover:bg-cyan-300 text-zinc-950 font-semibold transition-colors"
              >
                View Projects
                <ArrowDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
              </a>
              <button
                type="button"
                onClick={openIntroVideo}
                className="inline-flex items-center gap-2 px-6 h-12 rounded-xl glass-strong hover:border-cyan-400/30 text-white font-medium transition-colors"
              >
                <Play className="w-4 h-4 text-cyan-300" />
                Watch Intro
              </button>
              <a
                href={personalInfo.resumeUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-6 h-12 rounded-xl glass-strong hover:border-cyan-400/30 text-white font-medium transition-colors"
              >
                <Download className="w-4 h-4 text-cyan-300" />
                Resume.pdf
              </a>
              <a
                href={personalInfo.github}
                target="_blank"
                rel="noreferrer"
                className="w-12 h-12 rounded-xl glass-strong flex items-center justify-center text-zinc-300 hover:text-cyan-300 hover:border-cyan-400/30 transition-colors"
                aria-label="GitHub"
              >
                <Github className="w-4 h-4" />
              </a>
              <a
                href={personalInfo.linkedin}
                target="_blank"
                rel="noreferrer"
                className="w-12 h-12 rounded-xl glass-strong flex items-center justify-center text-zinc-300 hover:text-cyan-300 hover:border-cyan-400/30 transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-4 h-4" />
              </a>
            </motion.div>

            {/* Command hint */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="mt-6 inline-flex items-center gap-2 text-zinc-500"
            >
              <Command className="w-3.5 h-3.5" />
              <span className="font-mono text-[11px]">
                press{" "}
                <kbd className="px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-zinc-300">
                  ⌘ K
                </kbd>{" "}
                to open command palette
              </span>
            </motion.div>

            {/* Live terminal */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.75 }}
              className="mt-8"
            >
              <div className="flex items-center gap-2 mb-3">
                <TerminalIcon className="w-3.5 h-3.5 text-cyan-300" />
                <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-500">
                  live terminal session
                </span>
              </div>
              <Terminal
                title="vedant@portfolio — zsh"
                lines={terminalLines}
              />
            </motion.div>
          </div>

          {/* RIGHT: Holographic Avatar Viewport */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="lg:col-span-5 relative z-0"
          >
            <div className="relative h-[520px] sm:h-[620px] lg:sticky lg:top-32">
              {/* Corner HUD brackets */}
              <div className="absolute top-0 left-0 w-10 h-10 border-t-2 border-l-2 border-cyan-400/60 rounded-tl-lg z-20" />
              <div className="absolute top-0 right-0 w-10 h-10 border-t-2 border-r-2 border-cyan-400/60 rounded-tr-lg z-20" />
              <div className="absolute bottom-0 left-0 w-10 h-10 border-b-2 border-l-2 border-cyan-400/60 rounded-bl-lg z-20" />
              <div className="absolute bottom-0 right-0 w-10 h-10 border-b-2 border-r-2 border-cyan-400/60 rounded-br-lg z-20" />

              {/* Top HUD strip */}
              <div className="absolute top-3 left-12 right-12 flex items-center justify-between z-20 pointer-events-none">
                <div className="flex items-center gap-2">
                  <motion.span
                    animate={{ opacity: [1, 0.3, 1] }}
                    transition={{ duration: 1.4, repeat: Infinity }}
                    className="w-1.5 h-1.5 rounded-full bg-cyan-300"
                  />
                  <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-cyan-300/90">
                    subject.vedant
                  </span>
                </div>
                <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-amber-300/80">
                  ● REC
                </span>
              </div>

              {/* Bottom HUD strip */}
              <div className="absolute bottom-3 left-12 right-12 flex items-center justify-between z-20 pointer-events-none">
                <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-zinc-500">
                  biometric.lock — stable
                </span>
                <span className="font-mono text-[9px] text-emerald-300">
                  ✓ identified
                </span>
              </div>

              {/* Main viewport */}
              <div className="absolute inset-0 rounded-3xl overflow-hidden bg-[#04050a]">
                {/* Radar rings */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  {[280, 380, 480].map((size, i) => (
                    <motion.div
                      key={size}
                      animate={{ rotate: i % 2 === 0 ? 360 : -360 }}
                      transition={{
                        duration: 18 + i * 6,
                        repeat: Infinity,
                        ease: "linear"
                      }}
                      style={{ width: size, height: size }}
                      className="absolute rounded-full border border-cyan-400/15"
                    >
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-[0_0_12px_2px_rgba(34,211,238,0.6)]" />
                    </motion.div>
                  ))}
                  {/* Inner dashed ring */}
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 24, repeat: Infinity, ease: "linear" }}
                    className="absolute w-[200px] h-[200px] rounded-full border border-dashed border-amber-400/30"
                  />
                </div>

                {/* Scanline sweep */}
                <motion.div
                  animate={{ y: [0, 620, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                  className="absolute inset-x-0 h-[2px] bg-gradient-to-r from-transparent via-cyan-300/80 to-transparent blur-[1px] pointer-events-none z-10"
                />

                {/* Cross-hair guides */}
                <div className="absolute inset-x-0 top-1/2 h-px bg-gradient-to-r from-transparent via-cyan-400/25 to-transparent pointer-events-none" />
                <div className="absolute inset-y-0 left-1/2 w-px bg-gradient-to-b from-transparent via-cyan-400/25 to-transparent pointer-events-none" />

                {/* Base glow */}
                <div className="absolute inset-x-12 bottom-12 h-32 rounded-full bg-cyan-500/20 blur-3xl pointer-events-none" />

                {/* 3D Avatar */}
                <div className="absolute inset-0">
                  <Avatar3D />
                </div>

                {/* Vignette */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#04050a] via-transparent to-transparent pointer-events-none" />
              </div>

              {/* Orbital data tags */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -left-6 top-24 z-30 hidden lg:block"
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-px bg-cyan-400/60" />
                  <div className="px-3 py-1.5 rounded-lg glass-strong glow-cyan">
                    <p className="font-mono text-[9px] tracking-wider text-cyan-300/70 uppercase">role</p>
                    <p className="font-mono text-[11px] text-cyan-200">fullstack.backend</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                animate={{ y: [0, 12, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -right-6 top-48 z-30 hidden lg:block"
              >
                <div className="flex items-center gap-2">
                  <div className="px-3 py-1.5 rounded-lg glass-strong glow-amber">
                    <p className="font-mono text-[9px] tracking-wider text-amber-300/70 uppercase">stack</p>
                    <p className="font-mono text-[11px] text-amber-200">spring + react</p>
                  </div>
                  <div className="w-8 h-px bg-amber-400/60" />
                </div>
              </motion.div>

              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
                className="absolute left-8 -bottom-2 z-30 hidden lg:block"
              >
                <div className="px-3 py-1.5 rounded-lg glass-strong glow-emerald">
                  <p className="font-mono text-[9px] tracking-wider text-emerald-300/70 uppercase">latency</p>
                  <p className="font-mono text-[11px] text-emerald-200">avg · &lt;200ms</p>
                </div>
              </motion.div>

              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
                className="absolute right-6 -bottom-2 z-30 hidden lg:block"
              >
                <div className="px-3 py-1.5 rounded-lg glass-strong">
                  <p className="font-mono text-[9px] tracking-wider text-zinc-400/70 uppercase">uptime</p>
                  <p className="font-mono text-[11px] text-cyan-200">99.98%</p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Tech strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 1 }}
          className="mt-16 pt-6 border-t border-white/5 flex flex-wrap items-center justify-between gap-4"
        >
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-600">
            stack
          </span>
          <div className="flex flex-wrap gap-2">
            {heroStack.map((t) => (
              <span
                key={t}
                className="px-3 py-1 rounded-full glass font-mono text-[11px] tracking-wide text-zinc-300 hover:text-cyan-300 hover:border-cyan-400/30 transition-colors"
              >
                {t}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
